import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, inject } from '@angular/core';

import { Router } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { UserStatus } from '../../shared/models/user.model';

@Component({
  selector: 'app-frozen-account',
  imports: [],
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: './frozen-account.html'
})
export class FrozenAccountComponent implements OnInit, OnDestroy {
  private statusCheckInterval: any;

  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  ngOnInit(): void {
    // Refresh from server immediately and check status
    this.refreshAndCheckStatus();

    // Set up periodic status check every 30 seconds
    this.statusCheckInterval = setInterval(() => {
      this.refreshAndCheckStatus();
    }, 30000);
  }

  ngOnDestroy(): void {
    // Clean up interval when component is destroyed
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
    }
  }

  private checkAccountStatus(): void {
    const currentUser = this.authService.getCurrentUser();

    if (!currentUser) {
      // No user data, redirect to login
      this.router.navigate(['/login']);
      return;
    }

    // If user is no longer frozen/inactive, redirect to appropriate page
    if (currentUser.status === UserStatus.Active) {
      const role = currentUser.systemRole ?? 0;
      const target = (role === 3 || role === 4) ? '/plugins' : '/team-management';
      this.router.navigate([target]);
      return;
    }

    // If user is deleted, also redirect to login
    if (currentUser.status === UserStatus.Deleted) {
      this.authService.logout().subscribe(() => {
        this.router.navigate(['/login']);
      });
      return;
    }

    // For pending users, redirect to login (they should use their invite link)
    if (currentUser.status === UserStatus.Pending) {
      this.router.navigate(['/login']);
      return;
    }
  }

  private refreshAndCheckStatus(): void {
    // Refresh user data from server and check status
    this.authService.refreshCurrentUser().subscribe({
      next: () => {
        this.checkAccountStatus();
      },
      error: (error) => {
        // If refresh fails with 403 or inactive account error, user is still frozen
        const isInactive = error.status === 403 || error.message?.includes('inactive') || error.error?.message?.includes('inactive');
        if (!isInactive) {
          // Other errors might mean session expired, redirect to login
          this.router.navigate(['/login']);
        }
      }
    });
  }
}